/*
 * Carissima support chat widget — vanilla JS, no dependencies.
 * Embed:
 *   <link rel="stylesheet" href="https://YOUR-APP.vercel.app/widget/carissima-chat.css">
 *   <script src="https://YOUR-APP.vercel.app/widget/carissima-chat.js"
 *           data-endpoint="https://YOUR-APP.vercel.app/api/chat"
 *           data-lang="auto" defer></script>
 * data-lang: "en" | "fr" | "auto" (auto reads <html lang>, defaults to fr for a QC-first brand).
 */
(function () {
  "use strict";
  // Works for a normal <script> tag (document.currentScript), a dynamically injected one
  // (look it up by src), or an explicit window.CARISSIMA_CHAT config object.
  var cfg = window.CARISSIMA_CHAT || {};
  var script = document.currentScript || document.querySelector('script[src*="carissima-chat.js"]');
  var ENDPOINT = cfg.endpoint || (script && script.getAttribute("data-endpoint")) || "/api/chat";
  var ESCALATE = ENDPOINT.replace(/\/[^\/]*$/, "/escalate");
  var LIVE = ENDPOINT.replace(/\/[^\/]*$/, "/live");
  var LANG_ATTR = cfg.lang || (script && script.getAttribute("data-lang")) || "auto";

  function detectLang() {
    if (LANG_ATTR === "en" || LANG_ATTR === "fr") return LANG_ATTR;
    var htmlLang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
    if (htmlLang.indexOf("en") === 0) return "en";
    return "fr"; // QC-first default
  }
  var LANG = detectLang();
  // Stable per-visit conversation id so the backend can log the transcript (Law 25).
  var CID = (function () {
    try {
      var k = sessionStorage.getItem("crsm_cid");
      if (k) return k;
      var v = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
      sessionStorage.setItem("crsm_cid", v);
      return v;
    } catch (e) { return Date.now() + "-" + Math.random().toString(36).slice(2); }
  })();

  var T = {
    en: {
      title: "Carissima",
      subtitle: "Here to help",
      greeting: "Hi! I'm here to help with anything about Carissima — sofas, fabrics, delivery, your order. What can I help you with?",
      placeholder: "Type your message…",
      consent: "Chats may be shared with our team to help you. We keep them only as long as needed.",
      error: "Something went wrong. Please try again, or email support@carissima.ca.",
      aria_open: "Open chat",
      aria_close: "Close chat",
      aria_send: "Send",
      talk: "Talk to a person",
      connecting: "Connecting you to our team — one moment…",
      connected: "You're now chatting with our team.",
      offline: "Our team is live from 8am to 11pm. Right now we're offline — leave your email and we'll follow up.",
      ended: "This conversation has ended. Type below to chat with our assistant again.",
      nudge: "Converse with us",
      aria_nudge_close: "Dismiss",
    },
    fr: {
      title: "Carissima",
      subtitle: "On est là pour vous aider",
      greeting: "Bonjour! Je suis là pour vous aider avec tout ce qui touche Carissima — sofas, tissus, livraison, votre commande. Qu'est-ce que je peux faire pour vous?",
      placeholder: "Écrivez votre message…",
      consent: "Les conversations peuvent être partagées avec notre équipe pour vous aider. Nous les conservons seulement le temps nécessaire.",
      error: "Une erreur est survenue. Réessayez, ou écrivez à support@carissima.ca.",
      aria_open: "Ouvrir la conversation",
      aria_close: "Fermer la conversation",
      aria_send: "Envoyer",
      talk: "Parler à un conseiller",
      connecting: "On vous connecte à notre équipe — un instant…",
      connected: "Vous discutez maintenant avec notre équipe.",
      offline: "Notre équipe est en ligne de 8 h à 23 h. Pour le moment nous sommes hors ligne — laissez votre courriel et nous ferons un suivi.",
      ended: "Cette conversation est terminée. Écrivez ci-dessous pour reparler à notre assistant.",
      nudge: "Conversez avec nous",
      aria_nudge_close: "Fermer",
    },
  }[LANG];

  var ICON_CHAT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
  var ICON_SEND = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';

  // --- State ---
  var messages = []; // {role, content} sent to the API (excludes the static greeting)
  var busy = false;

  // --- Build DOM ---
  var root = document.createElement("div");
  root.className = "crsm-chat";
  root.innerHTML =
    '<button class="crsm-chat__bubble" type="button" aria-label="' + T.aria_open + '">' + ICON_CHAT + "</button>" +
    '<div class="crsm-chat__panel" role="dialog" aria-label="' + T.title + '">' +
    '  <div class="crsm-chat__header">' +
    '    <div><div class="crsm-chat__title">' + T.title + '</div><div class="crsm-chat__subtitle">' + T.subtitle + "</div></div>" +
    '    <div class="crsm-chat__head-actions">' +
    '      <button class="crsm-chat__talk" type="button">' + T.talk + "</button>" +
    '      <button class="crsm-chat__close" type="button" aria-label="' + T.aria_close + '">&times;</button>' +
    "    </div>" +
    "  </div>" +
    '  <div class="crsm-chat__log" aria-live="polite"></div>' +
    '  <div class="crsm-chat__consent">' + T.consent + "</div>" +
    '  <form class="crsm-chat__form">' +
    '    <textarea class="crsm-chat__input" rows="1" placeholder="' + T.placeholder + '" aria-label="' + T.placeholder + '"></textarea>' +
    '    <button class="crsm-chat__send" type="submit" aria-label="' + T.aria_send + '">' + ICON_SEND + "</button>" +
    "  </form>" +
    "</div>";
  document.body.appendChild(root);

  var bubble = root.querySelector(".crsm-chat__bubble");
  var panel = root.querySelector(".crsm-chat__panel");
  var closeBtn = root.querySelector(".crsm-chat__close");
  var talkBtn = root.querySelector(".crsm-chat__talk");
  var nudge = root.querySelector(".crsm-chat__nudge");
  var nudgeClose = root.querySelector(".crsm-chat__nudge-close");
  var nudgeText = root.querySelector(".crsm-chat__nudge-text");
  var log = root.querySelector(".crsm-chat__log");
  var form = root.querySelector(".crsm-chat__form");
  var input = root.querySelector(".crsm-chat__input");
  var sendBtn = root.querySelector(".crsm-chat__send");

  var greeted = false;
  function open() {
    root.classList.add("crsm-chat--open");
    root.classList.add("crsm-chat--nudge-off"); // hide the nudge once the chat is opened
    if (!greeted) { addBubble("bot", T.greeting); greeted = true; }
    input.focus();
  }
  function close() { root.classList.remove("crsm-chat--open"); }
  bubble.addEventListener("click", open);
  closeBtn.addEventListener("click", close);

  // Nudge label ("Converse with us") next to the bubble — clickable to open, dismissible.
  function hideNudge(persist) {
    root.classList.add("crsm-chat--nudge-off");
    if (persist) { try { localStorage.setItem("crsm_nudge_off", "1"); } catch (e) {} }
  }
  try { if (localStorage.getItem("crsm_nudge_off") === "1") root.classList.add("crsm-chat--nudge-off"); } catch (e) {}
  if (nudgeClose) nudgeClose.addEventListener("click", function (e) { e.stopPropagation(); hideNudge(true); });
  if (nudgeText) nudgeText.addEventListener("click", open);

  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // Linkify bare URLs and emails so support@carissima.ca / page links are clickable.
  function linkify(safe) {
    safe = safe.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
    safe = safe.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1">$1</a>');
    return safe;
  }
  function addBubble(role, text) {
    var el = document.createElement("div");
    el.className = "crsm-chat__msg crsm-chat__msg--" + role;
    el.innerHTML = linkify(escapeHtml(text));
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }
  function showTyping() {
    var el = document.createElement("div");
    el.className = "crsm-chat__typing";
    el.innerHTML = "<span></span><span></span><span></span>";
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function setBusy(b) { busy = b; sendBtn.disabled = b; }

  // When the customer provides an email (the handoff step), forward the transcript to support.
  // The bot has already shown the consent line before asking for it. Fire-and-forget; the bot's
  // own reply confirms to the customer. Send once per unique email.
  var sentEmails = {};
  function maybeEscalate(userText) {
    var m = userText.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/);
    if (!m || sentEmails[m[0]]) return;
    sentEmails[m[0]] = true;
    fetch(ESCALATE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: m[0], transcript: messages }),
    }).catch(function () {});
  }

  // --- Live human handoff ---
  var live = null; // { id, cursor, timer, connected }
  var requestingHuman = false;

  function sysNote(text) {
    var el = document.createElement("div");
    el.className = "crsm-chat__sys";
    el.textContent = text;
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
  }
  function liveCall(payload) {
    return fetch(LIVE, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }
  function requestHuman() {
    if (live || requestingHuman) return;
    requestingHuman = true;
    liveCall({ action: "availability" }).then(function (d) {
      requestingHuman = false;
      if (d && d.available) startLive();
      else sysNote(T.offline); // outside hours / no agent → email handoff (they leave an email, maybeEscalate fires)
    }).catch(function () { requestingHuman = false; sysNote(T.offline); });
  }
  function startLive() {
    sysNote(T.connecting);
    liveCall({ action: "start", lang: LANG, transcript: messages }).then(function (d) {
      if (!d || !d.sessionId) { sysNote(T.offline); return; }
      live = { id: d.sessionId, cursor: d.cursor || 0, timer: 0, connected: false };
      live.timer = setInterval(pollLive, 2000);
    }).catch(function () { sysNote(T.offline); });
  }
  function pollLive() {
    if (!live) return;
    liveCall({ action: "poll", sessionId: live.id, cursor: live.cursor }).then(function (d) {
      if (!d) return;
      live.cursor = d.cursor;
      if (d.status !== "waiting" && !live.connected) { live.connected = true; sysNote(T.connected); }
      // Pull the typing dots first so any new messages render beneath them, then re-add
      // at the bottom if the agent is still composing.
      if (live.typingEl) { if (live.typingEl.parentNode) live.typingEl.parentNode.removeChild(live.typingEl); live.typingEl = null; }
      (d.messages || []).forEach(function (m) { if (m.from === "agent") addBubble("bot", m.text); });
      if (d.status === "closed") { if (live.timer) clearInterval(live.timer); live = null; sysNote(T.ended); return; }
      if (d.typing) live.typingEl = showTyping();
    }).catch(function () {});
  }
  talkBtn.addEventListener("click", requestHuman);

  async function send(text) {
    if (busy || !text.trim()) return;
    addBubble("user", text);
    if (live) { liveCall({ action: "send", sessionId: live.id, text: text }).catch(function () {}); return; }
    messages.push({ role: "user", content: text });
    maybeEscalate(text);
    setBusy(true);
    var typing = showTyping();
    try {
      var res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: messages, cid: CID, lang: LANG }),
      });
      var data = await res.json().catch(function () { return {}; });
      typing.remove();
      var reply = (data && data.reply) || T.error;
      addBubble("bot", reply);
      messages.push({ role: "assistant", content: reply });
    } catch (e) {
      typing.remove();
      addBubble("bot", T.error);
    } finally {
      setBusy(false);
      input.focus();
    }
  }

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    var text = input.value.trim();
    if (!text) return;
    input.value = "";
    input.style.height = "auto";
    send(text);
  });
  input.addEventListener("input", function () {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 96) + "px";
  });
  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); }
  });
})();
